import { Grid, useTheme } from "@mui/material";
import {
  TRADE_HISTORY_TABS,
  TRADE_SIDE,
  TRADE_TYPE,
} from "pages/BasicTrade/components/constants";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  getOpenOrderList,
  getTradeLast24HList,
} from "store/slices/trade/tradeSlice";

import ConfirmAcceptModal from "pages/BasicTrade/components/Modals/ConfirmAcceptModal";
import { LoadingButton } from "@mui/lab";
import { checkIfRulesAreAccepted } from "utils/helpers/checkIfRulesAreAccepted";
import { scrollSection } from "utils/helpers/scrollSection";
import tradeApi from "store/slices/trade/tradeApi";
import useAlertModal from "hooks/useAlertModal";
import useDispatch from "hooks/useDispatch";
import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import useNotify from "hooks/useNotify";
import { useState } from "react";
import { getBasketWalletList } from "store/slices/wallet/walletSlice";
import { useSelector } from "react-redux";

function SubmitTrade({
  getTradeAmountMax,
  sideId,
  isPrice,
  setAdvancedTradeInputKey,
  handleGetWalletInfo,
}) {
  const theme = useTheme();

  const notify = useNotify();
  const alert = useAlertModal();

  const dispatch = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const { handleSubmit, watch, reset } = useFormContext();
  const amount = watch("amount");
  const price = watch("price");

  const walletList = useSelector(
    (state) => state.wallet?.portfolioBasketMenu?.data
  );
  const walletListFiltered = walletList?.filter(
    (wallet) => wallet.isHaveBasket === true
  );

  const [searchParams] = useSearchParams();
  const tradeHistoryTab = +searchParams.get("tradeHistoryTab") || 0;
  const walletTypeId = +searchParams.get("walletTypeId") || 1;
  const pageNumber = searchParams.get("page") || 1;
  const pageSize = searchParams.get("perPage") || 10;
  const allSearchParams = Object.fromEntries([...searchParams]);
  const basketId =
    +searchParams.get("basketId") || walletListFiltered?.[0]?.basketId || 0;
  const portfolioId =
    +searchParams.get("portfolioId") ||
    walletListFiltered?.[0]?.portfolioId ||
    0;

  const [loading, setLoading] = useState(false);
  const [tradeInsertInitial, setTradeInsertInitial] = useState(null);
  const [showConfirmAcceptModal, setConfirmAcceptModal] = useState(false);

  const onSubmit = async (payload) => {
    const body = {
      ...payload,
      price: payload?.price || 0,
    };

    try {
      setLoading(true);

      const { data } = await tradeApi.tradeInsertInitial(body);
      setAdvancedTradeInputKey(v => v + 1)
      updateTradeHistory();
      dispatch(getBasketWalletList({ id: +basketId, portfolioId }));
      setTradeInsertInitial(data);
      setConfirmAcceptModal(true);
    } catch (error) {
      checkIfRulesAreAccepted({ error, notify, alert, navigate });
    }

    setLoading(false);
  };

  const updateTradeHistory = () => {
    if (tradeHistoryTab === TRADE_HISTORY_TABS.openOrder) {
      dispatch(getOpenOrderList({ pageNumber, pageSize }));
    } else {
      dispatch(getTradeLast24HList());
    }
  };

  const handleCloseConfirmAcceptModal = () => {
    const isMarketTrade =
      tradeInsertInitial.data.typeId === TRADE_TYPE.market.id;

    navigate({
      pathname: location.pathname,
      search: `?${createSearchParams({
        ...allSearchParams,
        tradeHistoryTab: isMarketTrade
          ? TRADE_HISTORY_TABS.log24
          : TRADE_HISTORY_TABS.openOrder,
      })}`,
    });

    setConfirmAcceptModal(false);

    scrollSection("trade_history");
    getTradeAmountMax();
    handleGetWalletInfo(walletTypeId);
  };

  const isConfirmDisabled = useMemo(() => {
    if (isPrice) {
      return !amount || !price;
    } else {
      return !amount;
    }
  }, [amount, isPrice, price]);

  const buttonColor = useMemo(() => {
    if (sideId === TRADE_SIDE.Buy.id) return TRADE_SIDE.Buy.color;
    return TRADE_SIDE.Sell.color;
  }, [sideId]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={isConfirmDisabled}
            fullWidth
            color={buttonColor}
            onClick={handleSubmit(onSubmit)}
          >
            Confirm{" "}
            {sideId === TRADE_SIDE.Buy.id
              ? TRADE_SIDE.Buy.name
              : TRADE_SIDE.Sell.name}
          </LoadingButton>
        </Grid>
      </Grid>

      <ConfirmAcceptModal
        color={theme.palette.success.main}
        open={showConfirmAcceptModal}
        onClose={handleCloseConfirmAcceptModal}
        tradeInsertInitial={tradeInsertInitial}
      />
    </>
  );
}

export default SubmitTrade;
